import React, { useEffect, useState } from "react";
import { get } from "lodash/fp";
import {
  Container,
  Panel,
  Pagination,
  Table,
  Footer,
  Content,
  Grid,
  Row,
  Col,
  Button,
  Form,
  Divider,
  SelectPicker,
  Modal,
  Loader,
} from "rsuite";
import { updateIn } from "json-immut";
import CustomCell from "../../components/Elements/CustomCell";
import CustomHeaderCell from "../../components/Elements/CustomHeaderCell";
import { RoleServices } from "../../../core/services/RoleServices";
import { UtilityServices } from "../../../core/services/UtilityServices";
import { useTranslation } from "react-i18next";
import VisibleIcon from "@rsuite/icons/Visible";
import "./Roles.css";
import {
  statusArr,
  appConstObj,
  messageTypeArr,
} from "../../../core/config/AppConstants";

function Roles() {
  const { Column } = Table;
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<any[]>([]);
  const [permissionList, setPermissionList] = useState<any>({});
  const [permissionsData, setPermissionData] = useState<any>({});
  const [total, setTotal] = useState(0);
  const [rows, setRows] = useState(10);
  const [page, setPage] = useState(1);
  const [sort_by, setSortBy] = useState();
  const [sort_order, setSortOrder] = useState();
  const [formData, setFormData] = useState({});
  const [displayFilter, setDisplayFilter] = useState(false);
  const [open, setOpen] = useState(false);
  const [permissionLoader, setPermissionLoader] = useState(false);
  const [permissionFor, setPermissionFor] = useState("");
  const [selectedPermissions, setSelectedPermissions] = useState<any>([]);
  const [viewRole, setViewRole] = useState<boolean>();
  const [manageRole, setManageRole] = useState<boolean>();
  const { t } = useTranslation();

  const roleTypes = [
    {
      label: t("BACKEND"),
      value: "b",
    },
    {
      label: t("CLIENT"),
      value: "c",
    },
    {
      label: t("FRONTEND"),
      value: "f",
    },
  ];

  const roleStatus = [
    {
      label: t("ACTIVE"),
      value: 1,
    },
    {
      label: t("INACTIVE"),
      value: 2,
    },
  ];

  const toggleFilter = () => {
    setDisplayFilter((value) => !value);
  };

  const handleClose = () => {
    setPermissionFor("");
    setOpen(false);
  };

  const handleChange = (data: any) => {
    let form = updateIn(formData, [data.path], data.value);
    setFormData(form);
  };

  async function getRoles(param: any) {
    try {
      setLoading(true);
      let params: any = { ...formData, ...param };
      const response: any = await RoleServices.roleList(params);
      setData(get("data.data", response));
      setTotal(get("data.total", response));
      if (!response.status) {
        UtilityServices.showToaster({
          message: response?.data?.message,
        });
      }
      setLoading(false);
    } catch (error: any) {
      console.log("error ", error);
      UtilityServices.showToaster({
        message: error?.message,
        type: messageTypeArr.error,
      });
      setLoading(false);
    }
  }

  async function fetchPermission(e: any, val: any) {
    try {
      e.preventDefault();
      setPermissionLoader(false);
      setOpen(true);
      setPermissionFor(val.id);
      const permissionResponse: any = await RoleServices.permissionList();
      setPermissionList(get("data.data", permissionResponse));
      if (!permissionResponse.status) {
        UtilityServices.showToaster({
          message: permissionResponse?.data?.message,
        });
      }

      const rolePermissionsResponse = await RoleServices.rolePermissionsList(
        val.name
      );

      setPermissionData(get("data.data", rolePermissionsResponse));
      setPermissionLoader(true);

      if (!rolePermissionsResponse.status) {
        UtilityServices.showToaster({
          message: rolePermissionsResponse?.data?.message,
        });
      }
    } catch (error: any) {
      console.log("error ", error);
      UtilityServices.showToaster({
        message: error?.message,
        type: messageTypeArr.error,
      });
    }
  }

  async function savePermissions() {
    try {
      setLoading(true);
      let payload = {
        roles_id: permissionFor,
        permissions_id: selectedPermissions.toString(),
      };
      const response: any = await RoleServices.replaceRolePermissions(payload);
      if (response.status) {
        UtilityServices.showToaster({
          message: response?.data?.message,
        });
        getRoles({
          page: 1,
          rows: rows,
          sort_by: sort_by,
          sort_order: sort_order,
        });
      } else {
        UtilityServices.showToaster({
          message: response?.data?.message,
        });
      }
      setLoading(false);
      setOpen(false);
    } catch (error: any) {
      console.log("error ", error);
      UtilityServices.showToaster({
        message: error?.message,
        type: messageTypeArr.error,
      });
      setLoading(false);
    }
  }

  const checkAssigned = (rowData: any) => {
    if (permissionsData.has_all_permissions == "1") {
      return true;
    } else {
      const res = permissionsData.permissions.filter(
        (e: any) => e.id === rowData.id && e.name === rowData.name
      );
      return res.length > 0 ? true : false;
    }
  };

  function onPageChange(page: number) {
    setPage(page);
    getRoles({
      page: page,
      rows: rows,
      sort_by: sort_by,
      sort_order: sort_order,
    });
  }

  function onSortChange(dataKey: any, sortType: any) {
    setPage(1);
    setSortBy(dataKey);
    setSortOrder(sortType);
    getRoles({
      page: 1,
      rows: rows,
      sort_by: dataKey,
      sort_order: sortType,
    });
  }

  const handleChangeRows = (rows: any) => {
    setPage(1);
    setRows(rows);
    getRoles({
      page: 1,
      rows: rows,
      sort_by: sort_by,
      sort_order: sort_order,
    });
  };

  async function init() {
    try {
      setViewRole(UtilityServices.hasPermission("view-role"));
      setManageRole(UtilityServices.hasPermission("manage-role"));
    } catch (error: any) {
      console.log(error);
    }
  }

  useEffect(() => {
    init();
    getRoles({
      page: page,
      rows: rows,
      sort_by: sort_by,
      sort_order: sort_order,
    });
  }, []);

  useEffect(() => {
    if (typeof permissionsData.permissions !== "undefined") {
      const defaultSelectedPerm = permissionsData.permissions
        .filter((item: any) => {
          return item.status === 1;
        })
        .map((item: any) => {
          return item.id;
        });

      setSelectedPermissions(defaultSelectedPerm);
    }
  }, [permissionsData]);

  return (
    <div className="users roles-filter">
      <Container className="container">
        <Panel shaded bordered>
          <Grid fluid>
            <Row className="show-grid">
              <Col md={12}>
                <h4 className="mb-3">{t("ROLE_LIST")}</h4>
              </Col>
              <Col md={10} mdPush={10}>
                <Button appearance="default" size="sm" onClick={toggleFilter}>
                  {t("FILTER")}
                </Button>
              </Col>
            </Row>
          </Grid>

          {displayFilter ? (
            <Container>
              <Form onSubmit={getRoles} layout="inline" className="role-fields mb-3 content pt-0">
                <Form.Group controlId="type-7">
                  <Form.ControlLabel>{t("TYPE")}</Form.ControlLabel>
                  <SelectPicker
                    data={roleTypes}
                    name="role_type"
                    style={{ width: 224 }}
                    onChange={(v) =>
                      handleChange({ path: "role_type", value: v })
                    }
                    onClean={(v) =>
                      handleChange({ path: "role_type", value: "" })
                    }
                  />
                </Form.Group>

                <Form.Group controlId="status-7">
                  <Form.ControlLabel>{t("STATUS")}</Form.ControlLabel>
                  <SelectPicker
                    data={roleStatus}
                    name="role_status"
                    style={{ width: 224 }}
                    onChange={(v) => handleChange({ path: "status", value: v })}
                    onClean={(v) => handleChange({ path: "status", value: "" })}
                  />
                </Form.Group>
                <Form.Group>
                  <div className="role-btn-search mt-4">
                    <Button appearance="primary" type="submit" className="mt-2">
                      {t("SEARCH")}
                    </Button>
                  </div>
                </Form.Group>
              </Form>
            </Container>
          ) : (
            ""
          )}

          <Content>
            <Table
              loading={loading}
              height={400}
              fillHeight={true}
              data={data}
              hover={true}
              onSortColumn={onSortChange}
              sortColumn={sort_by}
              sortType={sort_order}
              bordered
              cellBordered
            >
              <Column flexGrow={1} align="left" resizable sortable fixed>
                <CustomHeaderCell>{t("NAME")}</CustomHeaderCell>
                <CustomCell dataKey="name" />
              </Column>

              <Column flexGrow={1} align="center" sortable>
                <CustomHeaderCell>{t("LABEL")}</CustomHeaderCell>
                <CustomCell dataKey="label" />
              </Column>

              <Column flexGrow={1} align="center">
                <CustomHeaderCell>{t("TYPE")}</CustomHeaderCell>
                <CustomCell dataKey="role_type">
                  {(rowData: any) => {
                    if (rowData.role_type == "b") {
                      return <span>{t("BACKEND")}</span>;
                    } else if (rowData.role_type == "f") {
                      return <span>{t("FRONTEND")}</span>;
                    } else {
                      return <span>{t("CLIENT")}</span>;
                    }
                  }}
                </CustomCell>
              </Column>

              <Column flexGrow={1} align="center">
                <CustomHeaderCell>{t("PERMISSIONS")}</CustomHeaderCell>
                <CustomCell dataKey="has_all_permissions">
                  {(rowData: any) => {
                    if (rowData.has_all_permissions == 1) {
                      return <span>{t("ALL")}</span>;
                    } else {
                      return <span>{t("LIMITED")}</span>;
                    }
                  }}
                </CustomCell>
              </Column>

              <Column align="center" width={100}>
                <CustomHeaderCell>{t("SUPERADMIN")}</CustomHeaderCell>
                <CustomCell dataKey="is_super_admin">
                  {(rowData: any) => {
                    if (rowData.is_super_admin == 1) {
                      return <span>{t("YES")}</span>;
                    } else {
                      return <span>{t("NO")}</span>;
                    }
                  }}
                </CustomCell>
              </Column>
              {viewRole ? (
                <Column flexGrow={1} align="center">
                  <CustomHeaderCell>{t("ACTION")}</CustomHeaderCell>
                  <CustomCell>
                    {(rowData: any) => {
                      return (
                        <a
                          href="javascript:void(0)"
                          onClick={(e) => fetchPermission(e, rowData)}
                          title={t("VIEW")}
                        >
                          <VisibleIcon />
                        </a>
                      );
                    }}
                  </CustomCell>
                </Column>
              ) : (
                ""
              )}
            </Table>
          </Content>
          <Footer className="footer">
            <Pagination
              total={total}
              prev
              next
              first
              last
              ellipsis
              boundaryLinks
              maxButtons={5}
              size="xs"
              layout={["total", "-", "limit", "|", "pager", "skip"]}
              limitOptions={[10, 30, 50, 100]}
              limit={rows}
              activePage={page}
              onChangePage={onPageChange}
              onChangeLimit={handleChangeRows}
            />
          </Footer>
        </Panel>
      </Container>

      <Modal
        size={"md"}
        open={open}
        overflow={true}
        onClose={handleClose}
        onExited={() => {
          setPermissionLoader(false);
        }}
      >
        <Modal.Header>
          <Modal.Title>
            <h4 className="text-center mb-2">{t("PERMISSIONS")}</h4>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {permissionLoader ? (
            <React.Fragment>
              <Grid fluid>
                <Row className="show-grid">
                  <Col md={6}>
                    <p className="mb-3">
                      <strong>{t("NAME")}:</strong> {permissionsData.name}
                    </p>
                  </Col>
                  <Col md={6}>
                    <p>
                      <strong>{t("LABEL")}:</strong> {permissionsData.label}
                    </p>
                  </Col>
                </Row>
              </Grid>
              <Table
                autoHeight={true}
                height={100}
                data={permissionList}
                bordered
                cellBordered
              >
                <Column flexGrow={1} align="left" fixed>
                  <CustomHeaderCell>{t("NAME")}</CustomHeaderCell>
                  <CustomCell dataKey="name" />
                </Column>

                <Column flexGrow={1} align="center" fixed>
                  <CustomHeaderCell>{t("LABEL")}</CustomHeaderCell>
                  <CustomCell dataKey="label" />
                </Column>

                <Column flexGrow={1} align="center">
                  <CustomHeaderCell>{t("IS_ASSIGNED")}</CustomHeaderCell>
                  <CustomCell>
                    {(rowData: any) => {
                      return (
                        <input
                          type="checkbox"
                          value={rowData.id}
                          defaultChecked={checkAssigned(rowData)}
                          disabled={permissionsData.has_all_permissions == "1"}
                          onChange={(e: any) => {
                            // add to list
                            if (e.target.checked) {
                              setSelectedPermissions([
                                ...selectedPermissions,
                                rowData.id,
                              ]);
                            } else {
                              // remove from list
                              setSelectedPermissions(
                                selectedPermissions.filter(
                                  (perm: any) => perm !== rowData.id
                                )
                              );
                            }
                          }}
                        ></input>
                      );
                    }}
                  </CustomCell>
                </Column>
              </Table>
            </React.Fragment>
          ) : (
            <div style={{ textAlign: "center" }}>
              <Loader size="md" />
            </div>
          )}
        </Modal.Body>
        <Modal.Footer className="text-center mt-2">
          <Button onClick={handleClose} appearance="subtle">
            {t("CANCEL")}
          </Button>
          {manageRole ? (
            <Button
              onClick={savePermissions}
              appearance="primary"
              disabled={permissionsData.has_all_permissions == "1"}
            >
              {t("OK")}
            </Button>
          ) : (
            ""
          )}
        </Modal.Footer>
      </Modal>
    </div>
  );
}

export default Roles;
